import { Component } from '@theme/component';
import { prefersReducedMotion } from '@theme/utilities';

/**
 * @typedef {Object} TickerLoopRefs
 * @property {HTMLElement} track The `.ticker__track` list
 */

/** Upper bound on content copies needed to fill a wide screen */
const MAX_FILLS = 8;

/**
 * Endless horizontal ticker (`.ticker`). Repeats the track content until it fills the width,
 * appends an `aria-hidden` copy so the CSS loop is seamless, pauses offscreen and while focused.
 * With reduced motion the static track is left as is.
 *
 * Markup: `<ticker-loop class="ticker" on:focusin="/pause" on:focusout="/resume">` with
 * `<ul class="ticker__track" ref="track">` inside.
 *
 * @extends {Component<TickerLoopRefs>}
 */
class TickerLoop extends Component {
  requiredRefs = ['track'];

  /** @type {IntersectionObserver | null} */
  #observer = null;

  connectedCallback() {
    super.connectedCallback();
    if (prefersReducedMotion()) return;
    if (!this.classList.contains('is-running')) this.#build();

    this.#observer = new IntersectionObserver(([entry]) => {
      if (entry) this.classList.toggle('is-paused', !entry.isIntersecting);
    });
    this.#observer.observe(this);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#observer?.disconnect();
    this.#observer = null;
  }

  pause() {
    this.classList.add('is-paused');
  }

  resume() {
    this.classList.remove('is-paused');
  }

  #build() {
    const track = this.refs.track;
    const base = Array.from(track.children);
    for (let fills = 0; track.scrollWidth < this.offsetWidth && fills < MAX_FILLS; fills += 1) {
      for (const node of base) track.appendChild(node.cloneNode(true));
    }

    const copy = /** @type {HTMLElement} */ (track.cloneNode(true));
    copy.removeAttribute('ref');
    copy.setAttribute('aria-hidden', 'true');
    for (const link of copy.querySelectorAll('a')) link.setAttribute('tabindex', '-1');
    this.appendChild(copy);
    this.classList.add('is-running');
  }
}

if (!customElements.get('ticker-loop')) {
  customElements.define('ticker-loop', TickerLoop);
}
